import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Chip,
  Stack, Grid, CircularProgress, Checkbox, FormControlLabel,
  Card, CardContent, Divider, Tooltip, ToggleButtonGroup, ToggleButton,
  Collapse, IconButton
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon, FilterList as FilterIcon,
  MeetingRoom as RoomIcon, CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon, ViewList as TableListIcon,
  GridView as GridViewIcon, EventRepeat as RepeatIcon,
  DateRange as DateRangeIcon, CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Close as CloseIcon, EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable, { ColumnConfig } from '../../Commun/components/GenericTable';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { academyStore } from '../services/academyStore';
import { planningTheoriqueApi } from '../services/planningTheoriqueApi';
import { PlanningTheorique } from '../types/planningTheorique';

const formatToday = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatNextMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const PlanningGenerationPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useLanguage();
  const [, setTick] = useState(0);

  const [viewMode, setViewMode] = useDefaultViewMode();

  // Résultat de génération
  const [generationResult, setGenerationResult] = useState<{
    generatedCount: number;
    skippedCount: number;
    startDate: string;
    endDate: string;
    timestamp: string;
  } | null>(null);

  // Filtres
  const [startDate, setStartDate] = useState<string>(formatToday());
  const [endDate, setEndDate] = useState<string>(formatNextMonth());
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);

  // Données
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [plannings, setPlannings] = useState<PlanningTheorique[]>([]);
  const [selectedTheoIds, setSelectedTheoIds] = useState<number[]>([]);

  useEffect(() => {
    academyStore.ensureRoomsLoaded();
    return academyStore.subscribe(() => setTick(tick => tick + 1));
  }, []);

  const rooms = academyStore.getRooms();

  // Chargement des plannings théoriques selon filtres
  const handleLoad = async () => {
    if (!startDate || !endDate) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.validation.dates_required'));
      return;
    }
    if (endDate < startDate) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.validation.invalid_date_range'));
      return;
    }

    setLoading(true);
    try {
      const data = await planningTheoriqueApi.getPlanningTheoriques();

      // Filtrer selon les salles sélectionnées et recouvrement de dates
      const filtered = data.filter(p => {
        if (selectedRoomIds.length > 0 && !selectedRoomIds.includes(p.arm_idf)) {
          return false;
        }
        if (startDate && p.end_date && p.end_date < startDate) {
          return false;
        }
        if (endDate && p.start_date > endDate) {
          return false;
        }
        return true;
      });

      setPlannings(filtered);
      // Sélectionner tout par défaut pour faciliter le workflow
      setSelectedTheoIds(filtered.map(p => p.id));
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors du chargement des plannings.');
    } finally {
      setLoading(false);
    }
  };

  // Sélection globale ou unitaire
  const handleToggleSelectAll = () => {
    if (selectedTheoIds.length === plannings.length) {
      setSelectedTheoIds([]);
    } else {
      setSelectedTheoIds(plannings.map(p => p.id));
    }
  };

  const handleToggleTheo = (id: number) => {
    setSelectedTheoIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Génération du planning concret
  const handleGenerate = async () => {
    if (selectedTheoIds.length === 0) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.select_at_least_one'));
      return;
    }

    if (!startDate || !endDate) {
      blobticMessage.warning(t('commun.warning'), t('academy.planning_theorique.validation.dates_required'));
      return;
    }

    const confirmed = await blobticMessage.confirm({
      title: t('commun.confirmation'),
      message: t('academy.planning_theorique.gen_confirm', startDate, endDate) ||
        `Voulez-vous générer les séances réelles pour les ${selectedTheoIds.length} planning(s) sélectionné(s) sur la période du ${startDate} au ${endDate} ?`,
      severity: 'question'
    });

    if (!confirmed) return;

    setGenerating(true);
    try {
      const res = await planningTheoriqueApi.generatePlanning({
        theoIds: selectedTheoIds,
        startDate,
        endDate
      });

      // Synchroniser IndexedDB cache et stores React pour les séances
      await BlobticDataManager.removeStores(['academy_session']);
      academyStore.notify(['academy_session']);

      setGenerationResult({
        generatedCount: res.generatedCount,
        skippedCount: res.skippedCount,
        startDate,
        endDate,
        timestamp: new Date().toLocaleTimeString()
      });

      const successMsg = res.message || (res.skippedCount > 0
        ? t('academy.planning_theorique.generation_success_with_skipped', res.generatedCount, res.skippedCount)
        : t('academy.planning_theorique.generation_success_msg', res.generatedCount));

      blobticMessage.alert({
        title: t('commun.success'),
        message: successMsg,
        severity: 'success'
      });
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors de la génération.');
    } finally {
      setGenerating(false);
    }
  };

  // Colonnes pour GenericTable
  const tableColumns: ColumnConfig[] = [
    {
      id: 'selection',
      label: '',
      render: (row: PlanningTheorique) => (
        <Checkbox
          size="small"
          checked={selectedTheoIds.includes(row.id)}
          onChange={() => handleToggleTheo(row.id)}
        />
      )
    },
    {
      id: 'arm_idf',
      label: t('academy.planning_theorique.room'),
      render: (row: PlanningTheorique) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <RoomIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={700}>
            {row.room_number ? `${row.room_number} ${row.room_lib ? `(${row.room_lib})` : ''}` : `Salle #${row.arm_idf}`}
          </Typography>
        </Stack>
      )
    },
    {
      id: 'nbr_week',
      label: t('academy.planning_theorique.nbr_weeks'),
      render: (row: PlanningTheorique) => (
        <Chip
          icon={<RepeatIcon fontSize="small" />}
          label={`${row.nbr_week} ${row.nbr_week > 1 ? t('academy.planning_theorique.weeks') : t('academy.planning_theorique.week')}`}
          size="small"
          color="secondary"
          variant="outlined"
        />
      )
    },
    {
      id: 'start_date',
      label: t('academy.planning_theorique.period'),
      render: (row: PlanningTheorique) => (
        <Typography variant="body2">
          {row.start_date} → {row.end_date || t('academy.planning_theorique.indefinite')}
        </Typography>
      )
    },
    {
      id: 'day_type',
      label: t('academy.planning_theorique.day_type'),
      render: (row: PlanningTheorique) => {
        const isHoliday = row.day_type === 'Holiday';
        return (
          <Chip
            label={isHoliday ? t('academy.planning_theorique.type_holiday') : t('academy.planning_theorique.type_normal')}
            size="small"
            color={isHoliday ? 'warning' : 'success'}
          />
        );
      }
    },
    {
      id: 'vacs_count',
      label: t('academy.planning_theorique.vacs_count'),
      render: (row: PlanningTheorique) => (
        <Chip label={`${row.vacs_count || 0} créneau(x)`} size="small" variant="filled" />
      )
    }
  ];

  return (
    <GenericPage
      title={t('academy.planning_theorique.generation_title')}
      icon={<GenerateIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => { if (val) setViewMode(val); }}
            size="small"
          >
            <ToggleButton value="grid">
              <Tooltip title={t('commun.view_grid')}>
                <GridViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title={t('commun.view_table')}>
                <TableListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            color="primary"
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <GenerateIcon />}
            disabled={generating || selectedTheoIds.length === 0}
            onClick={handleGenerate}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {t('academy.planning_theorique.generate_button')} ({selectedTheoIds.length})
          </Button>
        </Stack>
      }
    >
      <Box sx={{ pb: 4 }}>
        {/* ── Bannière Résultat de Génération ── */}
        <Collapse in={!!generationResult}>
          {generationResult && (
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 3,
                bgcolor: isDark ? 'rgba(46, 125, 50, 0.18)' : '#e8f5e9',
                border: '1.5px solid',
                borderColor: isDark ? 'success.dark' : 'success.main',
                position: 'relative'
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: 'success.main', color: '#fff', display: 'flex' }}>
                    <EventAvailableIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="success.dark">
                      {t('academy.planning_theorique.generation_success_title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('academy.planning_theorique.generation_success_subtitle', generationResult.generatedCount, generationResult.startDate, generationResult.endDate)}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                      <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label={`${generationResult.generatedCount} ${t('academy.planning_theorique.sessions_generated')}`}
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      {generationResult.skippedCount > 0 && (
                        <Chip
                          label={`${generationResult.skippedCount} ${t('academy.planning_theorique.conflicts_skipped')}`}
                          color="warning"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                      <Chip
                        label={`${t('academy.planning_theorique.period') || 'Période'} : ${generationResult.startDate} → ${generationResult.endDate}`}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`${t('common.date_time') || 'Heure'} : ${generationResult.timestamp}`}
                        variant="outlined"
                        size="small"
                      />
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CalendarIcon />}
                    onClick={() => navigate('/Academy/scheduler')}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    {t('academy.planning_theorique.view_scheduler')}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => setGenerationResult(null)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Collapse>

        {/* ── Barre de filtres ── */}
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3, bgcolor: 'background.paper' }}>
          <Grid container spacing={2} alignItems="center">
            {/* 1. Date Début Cible */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                fullWidth
                size="small"
                required
                label={t('academy.planning_theorique.target_start_date')}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 2. Date Fin Cible */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                fullWidth
                size="small"
                required
                label={t('academy.planning_theorique.target_end_date')}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 3. Salles Multi-Select */}
            <Grid item xs={12} sm={8} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: any) => {
                    const sel = selected as number[];
                    if (sel.length === 0) return <em>{t('academy.planning_theorique.all_rooms')}</em>;
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sel.map(id => {
                          const r = rooms.find(x => x.id === id);
                          return <Chip key={id} label={r?.room_number || `#${id}`} size="small" />;
                        })}
                      </Box>
                    );
                  }
                }}
                label={t('academy.planning_theorique.room_filter')}
                value={selectedRoomIds}
                onChange={(e) => setSelectedRoomIds(typeof e.target.value === 'string' ? [] : (e.target.value as number[]))}
              >
                {rooms.map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    <Checkbox checked={selectedRoomIds.includes(r.id)} size="small" />
                    {r.room_number} {r.floor ? `(${r.floor})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 4. Bouton Charger */}
            <Grid item xs={12} sm={4} md={2}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FilterIcon />}
                disabled={loading}
                onClick={handleLoad}
                sx={{ borderRadius: 2, height: 40 }}
              >
                {t('academy.planning_theorique.load_btn')}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* ── Barre de sélection ── */}
        {plannings.length > 0 && (
          <Paper
            elevation={1}
            sx={{
              p: 1.5,
              mb: 2.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.05),
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedTheoIds.length === plannings.length && plannings.length > 0}
                  indeterminate={selectedTheoIds.length > 0 && selectedTheoIds.length < plannings.length}
                  onChange={handleToggleSelectAll}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {selectedTheoIds.length === plannings.length
                    ? t('academy.planning_theorique.all_selected')
                    : `${selectedTheoIds.length} / ${plannings.length} ${t('academy.planning_theorique.plannings_selected')}`}
                </Typography>
              }
            />
          </Paper>
        )}

        {/* ── Affichage Grille vs Tableau ── */}
        {viewMode === 'grid' ? (
          <Grid container spacing={2.5}>
            {plannings.map((item) => {
              const isSelected = selectedTheoIds.includes(item.id);
              const isHoliday = item.day_type === 'Holiday';

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <Card
                    elevation={isSelected ? 3 : 1}
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      cursor: 'pointer',
                      border: `2px solid ${isSelected ? theme.palette.primary.main : 'transparent'}`,
                      bgcolor: isSelected ? alpha(theme.palette.primary.main, isDark ? 0.12 : 0.04) : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => handleToggleTheo(item.id)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => handleToggleTheo(item.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography variant="subtitle1" fontWeight={700}>
                            {item.room_number || `Salle #${item.arm_idf}`}
                          </Typography>
                        </Stack>

                        <Chip
                          label={isHoliday ? t('academy.planning_theorique.type_holiday') : t('academy.planning_theorique.type_normal')}
                          size="small"
                          color={isHoliday ? 'warning' : 'success'}
                        />
                      </Stack>

                      {item.room_lib && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
                          {item.room_lib}
                        </Typography>
                      )}

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={0.8} sx={{ ml: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <RepeatIcon fontSize="small" color="secondary" />
                          <Typography variant="body2">
                            Cycle : <strong>{item.nbr_week}</strong> {item.nbr_week > 1 ? 'semaines' : 'semaine'}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <DateRangeIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {item.start_date} → {item.end_date || t('academy.planning_theorique.indefinite')}
                          </Typography>
                        </Stack>

                        <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ mt: 0.5 }}>
                          {item.vacs_count || 0} créneau(x) configuré(s)
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}

            {plannings.length === 0 && !loading && (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                  <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('academy.planning_theorique.no_data_to_generate')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('academy.planning_theorique.no_data_to_generate_desc')}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        ) : (
          <GenericTable
            columns={tableColumns}
            data={plannings}
            loading={loading}
            searchPlaceholder={t('academy.planning_theorique.search_placeholder')}
          />
        )}
      </Box>
    </GenericPage>
  );
};

export default PlanningGenerationPage;
